<template>
  <transition name="expand">
    <div v-if="shouldShowSuggestions">
      <p>{{ $t('addressState.unverifiedMessage') }}</p>
      <br/>
      <p class="has-text-weight-bold">{{ $t('addressState.enteredAddress') }}: {{ address?.toSingleLineString() }}</p>
      <br/>
      <p>
        <span class="has-text-weight-bold is-size-6">{{ $t('addressState.suggested') }}</span>
        &nbsp;
        <span class="is-size-7"> {{ $t('addressState.selectAddress') }}</span>
      </p>
      <template v-if="suggestions.length > 0">
        <ul class="mt-2 suggestions-list">
          <li v-for="(suggestion, index) in suggestions"
              class="mb-3"
              :key="index">
            <a class="box has-text-dark is-clickable suggestion-item"
               :class="{'is-selected': selectedPlaceId === suggestion.placeId}"
               @click="selectSuggestion(suggestion)">
              <div class="has-text-weight-semibold">
                {{ suggestion.mainText }}
              </div>
              <div class="has-text-grey is-size-7">
                {{ suggestion.secondaryText }}
              </div>
            </a>
          </li>
        </ul>
      </template>
      <template v-else>
        <div class="is-flex is-align-items-center is-justify-content-center no-suggestions-container">
          <p>{{ $t('addressState.noSuggestions') }}</p>
        </div>
      </template>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { Address } from '@/interfaces/admin/address';
import { AddressStatus, PlaceSuggestion } from '@/interfaces/admin/places';
import { autocomplete, getPlaceDetails } from '@/controllers/global/places';
import { computed, onMounted, ref, watch } from 'vue'
import { debounce } from 'lodash';
import { postError } from '@/helpers/notification';
import i18n from '@/locales'

const props = defineProps({
  address: {
    type: Object as () => Address | null,
    default: null
  },
  addressStatus: {
    type: String as () => AddressStatus | null,
    default: null
  },
  sessionToken: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['suggestion-selected']);

/**
 * Helps to smooth experience when mounting and the delay with suggestions.
 */
const hasAttemptedFetch = ref(false);
const isLocked = ref(false);
const selectedPlaceId = ref<string | null>(null);
const suggestions = ref<PlaceSuggestion[]>([]);

const fetchSuggestions = debounce(async (query: string) => {
  // Don't fetch new suggestions if we're locked (a suggestion was selected)
  if (isLocked.value) {
    return;
  }

  if (query.length <= 3) {
    suggestions.value = [];
    selectedPlaceId.value = null;
    hasAttemptedFetch.value = false;
    return;
  }
  try {
    const response = await autocomplete(query, props.sessionToken);
    suggestions.value = response.suggestions || [];
    selectedPlaceId.value = null; // Reset selection when suggestions change
  } catch (error) {
    console.error('Failed to fetch address suggestions:', error);
    postError({ text: i18n.global.t('addressState.failedToFetchSuggestions') });
    suggestions.value = [];
    selectedPlaceId.value = null;
  } finally {
    hasAttemptedFetch.value = true;
  }
}, 750);

watch(() => props.address, (newAddress) => {
  if (props.addressStatus !== AddressStatus.VERIFIED && (newAddress?.lengthWithoutCountryCode() ?? 0) > 3) {
    fetchSuggestions(newAddress?.toSingleLineString() || '');
  }
}, { deep: true });

// Clear suggestions when address is verified
watch(() => props.addressStatus, (newStatus) => {
  if (newStatus === AddressStatus.VERIFIED) {
    suggestions.value = [];
    selectedPlaceId.value = null;
    isLocked.value = false;
    hasAttemptedFetch.value = false;
  }
});

const selectSuggestion = async (suggestion: PlaceSuggestion) => {
  selectedPlaceId.value = suggestion.placeId;
  isLocked.value = true; // Lock suggestions to prevent updates

  try {
    const addressData = await getPlaceDetails(suggestion.placeId, props.sessionToken);
    emit('suggestion-selected', addressData);
  } catch (error) {
    console.error('Failed to fetch place details:', error);
    postError({ text: i18n.global.t('addressState.failedToLoadDetails') });
    selectedPlaceId.value = null;
  } finally {
    isLocked.value = false;
  }
};

const shouldShowSuggestions = computed(() => {
  const addressLength = props.address?.lengthWithoutCountryCode() ?? 0;
  return addressLength > 3 && hasAttemptedFetch.value;
});

onMounted(() => {
  if (!props.address) return

  const addressLength = props.address?.lengthWithoutCountryCode() ?? 0;
  if (props.addressStatus !== AddressStatus.VERIFIED && addressLength > 3) {
    fetchSuggestions(props.address.toSingleLineString() || '');
  }
})
</script>

<style scoped lang="scss">
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-in-out;
  transform-origin: top;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.no-suggestions-container {
  min-height: 200px;

  @media screen and (max-width: 768px) {
    min-height: 120px;
    padding: 1rem;
  }

  @media screen and (max-width: 480px) {
    min-height: 80px;
    padding: 0.75rem;
  }
}

.suggestions-list {
  list-style: none;
  margin: 0;
  max-height: 325px;
  overflow-y: auto;
  padding: 0;
}

.suggestion-item {
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  box-shadow: unset;
  display: block;
  margin-left: 0.125em;
  margin-right: 0.125em;
  padding: 0.75rem 1rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: #b5b5b5;
    box-shadow: 0 0 0 0.125em rgba(54, 54, 54, 0.1);
  }

  &.is-selected {
    border-color: #b5b5b5;
    background-color: #dbdbdb;
  }
}
</style>
